import type { AxiosInstance } from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { apiErrorMessage } from "../lib/api.js";

/**
 * Redeem an email-verification link. `/verify-email?token=…` lands here.
 *
 * WHY THIS SCREEN HAD TO EXIST. The API's verify happens on `POST /auth/verify-email` — a
 * link in an email cannot POST, so a page must do it. Without this route the link fell
 * through to the login screen, the address was never verified, and login then refused it
 * as unverified: two failures that looked like one broken login.
 *
 * IT VERIFIES ON MOUNT, ONCE. The token is single-use; firing the request twice — which
 * React's strict-mode double-mount would do — spends it on the first call and shows a
 * "no longer valid" error on the second. A ref guards against that.
 */
export function VerifyEmailScreen({
  client,
  token,
}: {
  readonly client: AxiosInstance;
  readonly token: string;
}): React.JSX.Element {
  const [state, setState] = useState<"verifying" | "done" | "failed">("verifying");
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        await client.post("/auth/verify-email", { token });
        setState("done");
      } catch (caught) {
        setError(apiErrorMessage(caught, "This verification link is no longer valid."));
        setState("failed");
      }
    })();
  }, [client, token]);

  return (
    <main className="bg-(--page) flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm text-center">
        <CardHeader>
          <div className="mx-auto mb-1 flex size-10 items-center justify-center rounded-full">
            {state === "verifying" ? (
              <Loader2 className="text-primary size-6 animate-spin" />
            ) : state === "done" ? (
              <CheckCircle2 className="size-6 text-[var(--health-green)]" />
            ) : (
              <XCircle className="text-destructive size-6" />
            )}
          </div>
          <CardTitle className="text-lg">
            {state === "verifying" ? "Verifying your email…" : state === "done" ? "Email verified" : "Link expired"}
          </CardTitle>
          <CardDescription>
            {state === "verifying"
              ? "One moment."
              : state === "done"
                ? "Your email is confirmed. You can sign in now."
                : (error ?? "Request a new verification link and try again.")}
          </CardDescription>
        </CardHeader>
        {state !== "verifying" && (
          <CardContent>
            <Button asChild className="w-full">
              <a href="/">{state === "done" ? "Go to sign in" : "Back to sign in"}</a>
            </Button>
          </CardContent>
        )}
      </Card>
    </main>
  );
}
